/* eslint-disable react-hooks/exhaustive-deps */
import { useTheme } from "@emotion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, debounce, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery } from "@mui/material";
import Popup from "../../components/popups/popup";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SearchInput from "../../components/inputs/searchInput";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import CityDropdown from "../../components/dropdowns/cityDropdown";
import BranchService from "../../services/branchService";
import StreetDropdown from "../../components/dropdowns/streetDropdown";
import DeleteBranchModal from "../../components/modals/branch/deleteBranchModal";
import ViewBranchModal from "../../components/modals/branch/viewBranchModal";
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import UpdateBranchBox from "../../components/boxes/branch/updateBranchBox";
import AddBranchBox from "../../components/boxes/branch/addBranchBox";
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import OnOffSwitchForDeleted from "../../components/inputs/onOffSwitchForDeleted";
import { formatPrice } from "../../utils";

const Branches = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedStreet, setSelectedStreet] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });

    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState({});

    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [deleted, setDeleted] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleOpenView = (branch) => {
        setSelectedBranch(branch);
        setOpenView(true);
    };;
    const handleCloseView = () => setOpenView(false);

    const handleReload = async () => {
        setOpenUpdate(false);
        setHasMore(true);
        setPage(1);
        setSearchTerm('');
        setSelectedCity(null);
        setSelectedStreet(null);
        await fetchBranches();

    };

    const handleOpenUpdate = (branch) => {
        setOpenUpdate(true);
        setSelectedBranch(branch);
    };

    const handleOpenDelete = (branch) => {
        setSelectedBranch(branch);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSearchTerm('');
            setSelectedCity(null);
            setSelectedStreet(null);
            await fetchBranches();
        }
    };


    const fetchBranches = async (page = 1, name = '', cityId = null, streetId = null) => {
        try {
            if (isLoading) return;
            setIsLoading(true);
            const filters = { name, cityId, streetId, ...sortState, deleted };
            const data = await BranchService.fetchBranches(page, filters);

            if (data.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setBranches([]);
                }
            } else {
                setBranches((prevs) => (page === 1 ? data : [...prevs, ...data]));
            }
        } catch (error) {
            console.error('Error fetching streets:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedFetchBranches = useMemo(() =>
        debounce((name, cityId, streetId) => {
            setPage(1);
            setHasMore(true);
            fetchBranches(1, name, cityId, streetId);
        }, 1000),
        []
    );

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        debouncedFetchBranches(value, selectedCity?.id, selectedStreet?.id);
    };

    const resetFilter = () => {
        setSearchTerm('');
        setHasMore(true);
        setSelectedCity(null);
        setSelectedStreet(null);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (page === 1) fetchBranches(page, searchTerm, selectedCity?.id, selectedStreet?.id);
    }, [page]);

    useEffect(() => {
        if (page !== 1) {
            isFirstRender.current = true;
            setPage(1);
        }
        fetchBranches(1, searchTerm, selectedCity?.id, selectedStreet?.id);
    }, [selectedCity, selectedStreet, sortState, deleted]);

    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchBranches(page + 1, searchTerm, selectedCity?.id, selectedStreet?.id);
                setPage(prevPage => prevPage + 1);
            }
        }
    };

    return (
        <Box sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeleteBranchModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} branch={selectedBranch} />
                <ViewBranchModal open={openView} handleClose={handleCloseView} branch={selectedBranch} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}


            {isTablet && <Box>
                {
                    (selectedBranch && openUpdate) ?
                        <UpdateBranchBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} branchId={selectedBranch.id} />
                        :
                        <AddBranchBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} />
                }
            </Box>}

            {/* Filter row */}
            {
                isTablet ? <Box
                    sx={{
                        my: 2,
                        alignItems: 'center',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        backgroundColor: 'white',
                        borderRadius: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', height: 50 }}>
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'90%'}>
                            <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                        </Box>
                    </Box>
                    <Divider />

                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <CityDropdown
                            isFilter={true}
                            selectedCity={selectedCity}
                            setSelectedCity={setSelectedCity}
                        />
                    </Box>
                    <Divider />

                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <StreetDropdown
                            isFilter={true}
                            selectedStreet={selectedStreet}
                            setSelectedStreet={setSelectedStreet}
                            cityId={selectedCity && selectedCity.id}
                        />
                    </Box>
                    <Divider />

                    <Box sx={{ display: 'flex', alignItems: 'center', height: 50 }}>
                        <Box sx={{
                            ml: isEn ? 3 : 0
                        }}>
                            <OnOffSwitchForDeleted
                                checkDeleted={deleted}
                                setCheckDeleted={setDeleted} />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
                </Box>
                    : <Box
                        sx={{
                            my: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'25%'}>
                            <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'25%'}>
                            <CityDropdown
                                isFilter={true}
                                selectedCity={selectedCity}
                                setSelectedCity={setSelectedCity}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'25%'}>
                            <StreetDropdown
                                isFilter={true}
                                selectedStreet={selectedStreet}
                                setSelectedStreet={setSelectedStreet}
                                cityId={selectedCity && selectedCity.id}
                            />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                        <OnOffSwitchForDeleted
                            checkDeleted={deleted}
                            setCheckDeleted={setDeleted} />
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }


            <Box sx={{
                display: 'flex'
            }}>
                {!isTablet && <Box sx={{
                    width: '35%'
                }}>
                    {
                        (selectedBranch && openUpdate) ?
                            <UpdateBranchBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} branchId={selectedBranch.id} />
                            :
                            <AddBranchBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} />
                    }
                </Box>}

                {!isTablet && <Box width={10}></Box>}

                {/* Scrollable Table with Sticky Header */}
                <TableContainer
                    component={Paper}
                    ref={tableContainerRef}
                    onScroll={handleScroll}
                    sx={{
                        boxShadow: 'none',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        borderRadius: 2,
                        height: 'calc(100vh - 170px)',
                        overflowY: 'auto',
                        width: isTablet ? '100%' : '65%'
                    }}
                >
                    <Table stickyHeader aria-label="simple table">
                        <TableHead
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <TableRow>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('id')}
                                        {sortState.sortBy === 'id' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('name')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('name')}
                                        {sortState.sortBy === 'name' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('wallet')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('wallet')}
                                        {sortState.sortBy === 'wallet' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('street')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('street')}
                                        {sortState.sortBy === 'street' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                    {t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {branches.map((branch, index) => (
                                <TableRow
                                    key={branch.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{branch.id}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{branch.name}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(branch.wallet)}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{branch.street.name}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            border: 1,
                                            borderColor: theme.palette.customGrey.borderInput,
                                            backgroundColor: theme.palette.secondary.main,
                                            borderRadius: 1,
                                        }}>
                                            <Box sx={{
                                                px: 1.5,
                                                pt: 0.6,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenView(branch)}>
                                                <RemoveRedEyeOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.text.primary }} />
                                            </Box>
                                            <Divider orientation="vertical" flexItem />
                                            {branch.deletedAt === null && <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenUpdate(branch)}>
                                                <DriveFileRenameOutlineOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.text.primary }} />
                                            </Box>}
                                            {branch.deletedAt === null && <Divider orientation="vertical" flexItem />}
                                            <Box sx={{
                                                px: 1.5,
                                                pt: 0.45,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenDelete(branch)}>
                                                {branch.deletedAt === null ?
                                                    <DeleteOutlineOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.red.bg }} />
                                                    :
                                                    <ReplayOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.green.bg }} />
                                                }
                                            </Box>

                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>

        </Box>
    );
}

export default Branches;